import { useState, useEffect, useCallback } from 'react';
import { getCourseProgress, patchLastPage, completePage } from '../api/progress';
import { useAuth } from '@/contexts/AuthContext';

export function useCourseProgressApi(courseId?: string) {
  const { user } = useAuth();
  const studentId = user?.id;
  const [completedPageIds, setCompletedPageIds] = useState<string[]>([]);
  const [lastPageId, setLastPageId] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!courseId || !studentId) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const server = await getCourseProgress(courseId);
        if (!mounted) return;
        setCompletedPageIds(server?.completedPageIds ?? []);
        setLastPageId(server?.lastPageId ?? undefined);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load progress');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [courseId, studentId]);

  const updateLastPageId = useCallback(
    async (pageId: string) => {
      if (!courseId || !studentId) return;
      // optimistic UI
      setLastPageId(pageId);
      try {
        const server = await patchLastPage(courseId, pageId);
        setCompletedPageIds(server?.completedPageIds ?? []);
        setLastPageId(server?.lastPageId ?? pageId);
      } catch {
        // ignore for MVP
      }
    },
    [courseId, studentId]
  );

  const markComplete = useCallback(
    async (pageId: string) => {
      if (!courseId || !studentId) return;
      // optimistic local update
      setCompletedPageIds((prev) => (prev.includes(pageId) ? prev : [...prev, pageId]));
      setLastPageId(pageId);
      try {
        const server = await completePage(courseId, pageId);
        setCompletedPageIds(server?.completedPageIds ?? []);
        setLastPageId(server?.lastPageId ?? pageId);
      } catch {
        // ignore
      }
    },
    [courseId, studentId]
  );

  return {
    completedPageIds,
    lastPageId,
    loading,
    error,
    updateLastPageId,
    markComplete,
    refresh: async () => {
      if (!courseId || !studentId) return;
      try {
        const server = await getCourseProgress(courseId);
        setCompletedPageIds(server?.completedPageIds ?? []);
        setLastPageId(server?.lastPageId ?? undefined);
      } catch {
        // ignore
      }
    },
  };
}

